import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Component, Inject, OnDestroy, OnInit, DOCUMENT, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import type { Params } from '@angular/router';
import { take } from 'rxjs/operators';
import { LoggedInService } from '../logged-in.service';
import type { IUserSessionResponse } from '../logged-in.service';
import { LoginRedirectService, SSO_RETURN_URL_STORAGE_KEY } from '../login-redirect.service';
import { SsoOptionsService } from '../sso-options.service';
import { MatProgressBar } from '@angular/material/progress-bar';
import { CbreLogger, CbreLoggerToken } from '@cbre/logger';

const PERMANENT_REDIRECT = 308;

/**
 * Automatic re-entry into SSO after a failed token exchange.
 *
 * This page never shows an error view. With the identity-provider session still
 * alive, re-entering /login mints a fresh authorization code without any user
 * interaction, which is exactly what a failed exchange needs. In production the
 * failures are overwhelmingly replays of an old callback URL (restored tabs, the
 * Back button, browser history) whose single-use code has expired (AADSTS70008),
 * so an immediate silent retry simply logs the user in.
 *
 * The retry is rate-limited so a persistently failing exchange (backend cannot
 * reach the provider, bad client secret, the provider redirecting back with
 * ?error=) degrades to one round trip per minute instead of a hot loop: the first
 * failure re-enters immediately, consecutive failures wait 2s, 4s, 8s ... capped
 * at 60s. The counter lives in sessionStorage (per tab, survives the round trip
 * through the provider), is cleared by a successful exchange and forgotten after
 * ten quiet minutes.
 */
const LOGIN_RETRY_STATE_KEY = 'ng-sso-login-retry';
const LOGIN_RETRY_BASE_DELAY_MS = 2_000;
const LOGIN_RETRY_MAX_DELAY_MS = 60_000;
const LOGIN_RETRY_RESET_AFTER_MS = 10 * 60_000;

interface LoginRetryState {
  attempts: number;
  lastFailureAt: number;
}

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'ng-sso-asd-callback',
  templateUrl: './asd-callback.component.html',
  styles: [],
  imports: [MatProgressBar],
})
export class AsdCallbackComponent implements OnInit, OnDestroy {
  callbackParams: Params;

  private retryTimer: ReturnType<typeof setTimeout> | undefined;

  constructor(
    route: ActivatedRoute,
    private http: HttpClient,
    private loggedInService: LoggedInService,
    private ssoOptions: SsoOptionsService,
    private loginRedirect: LoginRedirectService,
    @Inject(DOCUMENT) private document: Document,
    @Inject(CbreLoggerToken) private logger: CbreLogger
  ) {
    this.callbackParams = route.snapshot.queryParams;
  }

  ngOnInit() {
    const { tokenExchangeUrl, successUrl = '', tokenExchangeContext } = this.ssoOptions.options;
    const { code, state, error, error_description: errorDescription } = this.callbackParams;
    // Setting alreadyRedirected true prevents the app from refreshing when entering the app for the first time without authentification.
    this.ssoOptions.alreadyRedirected = true;

    if (!code) {
      // Either a replay of a callback URL whose code was already stripped, or the
      // identity provider reporting a failure through the redirect (?error=...).
      // The exchange cannot succeed without a code, so skip the doomed request.
      this.logger.error(`login callback without code${error ? `: ${error} ${errorDescription ?? ''}` : ''}`);
      this.scheduleLoginRetry();
      return;
    }

    const storedReturnUrl = localStorage.getItem(SSO_RETURN_URL_STORAGE_KEY);
    localStorage.removeItem(SSO_RETURN_URL_STORAGE_KEY);
    // storedReturnUrl (localStorage, set by us right before leaving for SSO) is authoritative -
    // the backend downgrades any state containing query characters (?=&) to '/', so `state` is
    // only a last-resort fallback when localStorage itself is unavailable/empty.
    const isDeepLinkPath = (value: unknown): value is string => typeof value === 'string' && value.startsWith('/') && value !== '/';
    const returnUrl = storedReturnUrl ?? (isDeepLinkPath(state) ? state : null) ?? successUrl;
    const successLoginCallback = this.onLoginSuccess(returnUrl);
    const headers = { 'Access-Control-Allow-Origin': '*' };
    // The context carries the token-exchange timeout budget (see SsoOptions):
    // aborting this request strands a spent single-use code and forces a whole
    // extra trip through the identity provider.
    const options = { headers, params: { code }, context: tokenExchangeContext };
    this.http.get(tokenExchangeUrl, options).pipe(take(1)).subscribe({
      next: successLoginCallback,
      error: this.onLoginError,
    });
  }

  ngOnDestroy(): void {
    if (this.retryTimer !== undefined) {
      clearTimeout(this.retryTimer);
    }
  }

  private readonly onLoginSuccess =
    (successUrl: string) =>
    (userSessionResponse: IUserSessionResponse): void => {
      const { options } = this.ssoOptions;
      this.loggedInService.setSession(userSessionResponse);
      this.clearLoginRetryState();
      // The code is spent now: take it out of this history entry so that Back /
      // tab restore land on a code-less callback URL (which re-enters SSO with
      // the deep link) instead of replaying a dead code against the backend.
      this.clearSpentCallbackParamsFromUrl();
      // Refresh the whole page after setting session after login success forcing the app to use newly generated session.
      this.onPageRefresh(`${options.hostUrl}${successUrl}`);
    };

  private readonly onLoginError = (errorResponse: HttpErrorResponse & { code?: number }): void => {
    // Status only: the request URL carries the authorization code.
    this.logger.error(`login error: HTTP ${errorResponse.status} ${errorResponse.statusText ?? ''}`);
    // redirect response AND if message is a CEP url
    if (errorResponse.code === PERMANENT_REDIRECT && errorResponse.error?.message?.includes('miq.cbre.com')) {
      // message contains the redirected page
      this.document.location = errorResponse.error.message;
      return;
    }
    // The code in the URL is single-use and already spent (the exchange for it
    // just failed) - strip it so a refresh replays nothing. The backend's own
    // recovery for this case is a 307 Location redirect, which an XHR cannot
    // follow into the identity provider, so recovery has to happen here.
    this.clearSpentCallbackParamsFromUrl();
    this.scheduleLoginRetry();
  };

  /** Top-level navigation back into SSO, preserving the original deep link. */
  private scheduleLoginRetry(): void {
    const delay = this.nextLoginRetryDelay();
    const retry = () => this.loginRedirect.redirectWithState(this.callbackParams['state']);
    if (delay === 0) {
      retry();
      return;
    }
    this.logger.warn(`login: re-entering SSO in ${delay}ms`);
    this.retryTimer = setTimeout(retry, delay);
  }

  /** Records one more consecutive failure and returns how long to wait before re-entering SSO. */
  private nextLoginRetryDelay(): number {
    const now = Date.now();
    try {
      const raw = sessionStorage.getItem(LOGIN_RETRY_STATE_KEY);
      const previous: LoginRetryState | null = raw ? JSON.parse(raw) : null;
      const recent = previous !== null && now - previous.lastFailureAt < LOGIN_RETRY_RESET_AFTER_MS;
      const attempts = recent ? previous.attempts + 1 : 1;
      const next: LoginRetryState = { attempts, lastFailureAt: now };
      sessionStorage.setItem(LOGIN_RETRY_STATE_KEY, JSON.stringify(next));
      return attempts <= 1 ? 0 : Math.min(LOGIN_RETRY_BASE_DELAY_MS * 2 ** (attempts - 2), LOGIN_RETRY_MAX_DELAY_MS);
    } catch {
      // Without storage the loop guard cannot count across page loads: behave
      // as if the cap were already reached rather than retry blind.
      return LOGIN_RETRY_MAX_DELAY_MS;
    }
  }

  private clearLoginRetryState(): void {
    try {
      sessionStorage.removeItem(LOGIN_RETRY_STATE_KEY);
    } catch {
      // No storage - nothing to clear.
    }
  }

  /** Drops code / session_state from the current history entry, keeping only the deep link. */
  private clearSpentCallbackParamsFromUrl(): void {
    try {
      const state = this.callbackParams['state'];
      const query = state ? `?state=${encodeURIComponent(state)}` : '';
      this.document.defaultView?.history.replaceState(null, '', `${this.document.location.pathname}${query}`);
    } catch {
      // History API unavailable - a replay will just re-enter SSO.
    }
  }

  //This method was created for testing purposes
  private onPageRefresh(url: string): void {
    this.document.location.assign(url);
  }
}
